import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { FaPlus, FaTrash } from 'react-icons/fa6';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import Button from '../../components/buttons/Button';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import ModalLayout from '../../components/layouts/ModalLayout';
import CancelSaveButton from '../../components/misc/CancelSaveButton';
import DatePicker from '../../components/forms/DatePicker';
import DropdownSelect from '../../components/forms/DropdownSelect';
import ParticipantsPicker from '../../components/misc/ParticipantsPicker';
import { ConnectionIdentifier } from '../../../types/misc';

import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import formsService from '../../../services/community/formsService';
import { FormQuestionType } from '../../../models/forms.model';
import { CommunityRole } from '../../../types/community';

type QuestionDraft = {
  localId: string;
  type: FormQuestionType;
  label: string;
  mandatory: boolean;
  options?: { label: string }[];
  meta?: any;
};

function SortableQuestion({
  q, idx, total, updateQuestion, removeQuestion, t,
}: {
  q: QuestionDraft;
  idx: number;
  total: number;
  updateQuestion: (index: number, updater: Partial<QuestionDraft>) => void;
  removeQuestion: (index: number) => void;
  t: (key: string) => string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.localId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div ref={setNodeRef} role="listitem" style={style} className='w-full rounded-[12px] bg-secondary-200/70 p-4 transition text-primary-900'>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div {...attributes} {...listeners} className="mr-3 cursor-grab p-2 rounded hover:bg-secondary-100 select-none text-secondary-500" title={t('general.drag')}>⋮</div>
          <div className="flex items-center gap-3">
            <div className="font-medium">{`${idx + 1}.`}</div>
            <div className="font-medium">{t(`component.eventPollForm.types.${q.type}`)}</div>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="tertiary" className="p-2" onClick={() => removeQuestion(idx)} title={t('general.delete')} aria-label={t('general.delete')}>
            <FaTrash className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <div className="w-full">
          <Input type="text" value={q.label} onChange={(e) => updateQuestion(idx, { label: e.target.value })} placeholder={t('component.formBuilder.questionLabel')} />
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm text-secondary-400">{t('general.mandatory')}?</label>
          <input type="checkbox" checked={q.mandatory} onChange={(e) => updateQuestion(idx, { mandatory: e.target.checked })} />
        </div>

        {q.type === FormQuestionType.CHOICE && (
          <div className="flex flex-col gap-2 p-3 rounded">
            {(q.options || []).map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <Input type="text" value={opt.label} onChange={(e) => updateQuestion(idx, { options: q.options!.map((o, j) => (j === oi ? { label: e.target.value } : o)) })} />
                <Button variant="tertiary" className="text-sm px-2 py-1" onClick={() => updateQuestion(idx, { options: q.options!.filter((_, j) => j !== oi) })}>{t('general.delete')}</Button>
              </div>
            ))}
            <Button className="p-0 underline text-sm" onClick={() => updateQuestion(idx, { options: [...(q.options || []), { label: '' }] })}>{t('component.eventPollForm.addOption')}</Button>
            {q.type === FormQuestionType.CHOICE && (
              <div className="mt-2 w-full max-w-xs">
                <Labeled label={t('component.formBuilder.choice.maxAnswers')}>
                  <Input type="number" min={1} value={q.meta?.maxAnswers ?? 1} onChange={(e) => updateQuestion(idx, { meta: { ...(q.meta || {}), maxAnswers: e.target.value ? Number(e.target.value) : undefined } })} />
                </Labeled>
              </div>
            )}
          </div>
        )}

        {q.type === FormQuestionType.NUMBER && (
          <div className="flex gap-2 items-center mt-2">
            <Labeled label={t('component.formBuilder.number.min')}><Input type="number" value={q.meta?.min ?? ''} onChange={(e) => updateQuestion(idx, { meta: { ...(q.meta || {}), min: e.target.value ? Number(e.target.value) : undefined } })} /></Labeled>
            <Labeled label={t('component.formBuilder.number.max')}><Input type="number" value={q.meta?.max ?? ''} onChange={(e) => updateQuestion(idx, { meta: { ...(q.meta || {}), max: e.target.value ? Number(e.target.value) : undefined } })} /></Labeled>
          </div>
        )}

        {q.type === FormQuestionType.RATING && (
          <div className="flex gap-2 items-center mt-2">
            <div className="w-full max-w-xs">
              <Labeled label={t('component.formBuilder.rating.scale')}>
                <DropdownSelect<number>
                  options={[
                    { label: '1-3', value: 3 },
                    { label: '1-5', value: 5 },
                    { label: '1-10', value: 10 },
                  ]}
                  value={q.meta?.scale ?? 5}
                  setValue={(v) => updateQuestion(idx, { meta: { ...(q.meta || {}), scale: v ?? 5 } })}
                />
              </Labeled>
            </div>
          </div>
        )} 
      </div>
    </div>
  );
}

export default function CreateFormPage(): JSX.Element {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [responsesCount, setResponsesCount] = useState<number>(0);
  const [originalQuestionsNormalized, setOriginalQuestionsNormalized] = useState<any[] | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [resultsVisibility, setResultsVisibility] = useState<'PUBLIC'|'PRIVATE'|'ADMIN_ONLY'>('PUBLIC');
  const [participantMode, setParticipantMode] = useState<'EVERYONE'|'SELECTED'>('EVERYONE');
  const [participants, setParticipants] = useState<ConnectionIdentifier[]>([]);

  const { formId } = useParams();
  const isEdit = !!formId;

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const f = await formsService.get(formId!);
        setTitle(f.title ? String(f.title).trim() : '');
        setDescription(f.description || '');
        setDeadline(f.deadline ? new Date(f.deadline) : null);
        setResultsVisibility(f.resultsVisibility ?? 'PUBLIC');
        const loadedParticipants = (f.participants ?? []) as ConnectionIdentifier[];
        setParticipants(loadedParticipants);
        setParticipantMode(loadedParticipants.length > 0 ? 'SELECTED' : 'EVERYONE');
        const normalizedLoadedQuestions = (f.questions || []).map((q) => ({
          localId: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          type: q.type,
          label: q.label ? String(q.label).trim() : '',
          mandatory: !!q.mandatory,
          options: q.options ? q.options.map((o) => ({ label: o.label ? String(o.label).trim() : '' })) : undefined,
          meta: q.meta || undefined,
        }));
        setQuestions(normalizedLoadedQuestions);
        setResponsesCount((f.responses || []).length);
        setOriginalQuestionsNormalized(normalizedLoadedQuestions.map((q) => ({
          type: q.type,
          label: q.label,
          mandatory: q.mandatory,
          meta: q.meta ?? null,
          options: q.options ? q.options.map((o) => String(o.label).trim()).filter((l) => l !== '') : [],
        })) );
      } catch (e) {
        // ignore, form might not exist
      }
    })();
  }, [formId]);

  if (!community || community.role !== CommunityRole.ADMIN) return <></>;

  const addQuestion = (type: FormQuestionType) => {
    const q: QuestionDraft = {
      localId: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      label: '',
      mandatory: false,
      options: type === FormQuestionType.CHOICE ? [{ label: '' }, { label: '' }] : undefined,
      meta: type === FormQuestionType.NUMBER ? { min: undefined, max: undefined } : type === FormQuestionType.RATING ? { scale: 5 } : type === FormQuestionType.CHOICE ? { maxAnswers: 1 } : undefined,
    };
    setQuestions((s) => [...s, q]);
  };

  const updateQuestion = (index: number, updater: Partial<QuestionDraft>) => {
    setQuestions((s) => s.map((q, i) => (i === index ? { ...q, ...updater } : q)));
  };

  const removeQuestion = (index: number) => setQuestions((s) => s.filter((_, i) => i !== index));


  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over) return;
    if (active.id !== over.id) {
      setQuestions((s) => {
        const oldIndex = s.findIndex((x) => x.localId === active.id);
        const newIndex = s.findIndex((x) => x.localId === over.id);
        return arrayMove(s, oldIndex, newIndex);
      });
    }
  };


  const validate = (): string | null => {
    const errors: string[] = [];

    if (!String(title || '').trim()) errors.push(t('component.formBuilder.errors.emptyTitle'));
    if (questions.length === 0) errors.push(t('component.formBuilder.errors.noQuestions'));

    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i];
      if (!String(q.label || '').trim()) errors.push(`${t('component.formBuilder.errors.emptyQuestion')} (${i + 1})`);
      if (q.type === FormQuestionType.CHOICE) {
        if (!q.options || q.options.length < 2) errors.push(`${t('component.formBuilder.errors.emptyOption')} (${i + 1})`);
        if (q.options && q.options.some((o) => !String(o.label || '').trim())) errors.push(`${t('component.formBuilder.errors.emptyOption')} (${i + 1})`);
        const max = q.meta?.maxAnswers !== undefined && q.meta?.maxAnswers !== null ? Number(q.meta.maxAnswers) : 1;
        if (Number.isNaN(max) || max < 1 || (q.options && max > q.options.length)) errors.push(`${t('component.formBuilder.errors.invalidMaxAnswers')} (${i + 1})`);
      }
      if (q.type === FormQuestionType.NUMBER) {
        const min = q.meta?.min;
        const max = q.meta?.max;
        if (min !== undefined && max !== undefined && Number(min) > Number(max)) errors.push(`${t('component.formBuilder.errors.invalidNumberRange')} (${i + 1})`);
      }
      if (q.type === FormQuestionType.RATING) {
        const raw = q.meta && q.meta.scale !== undefined && q.meta.scale !== null ? q.meta.scale : undefined;
        const scale = raw !== undefined && raw !== '' ? Number(raw) : 5; // default to 5 when not set
        if (Number.isNaN(scale) || scale < 2) errors.push(`${t('component.formBuilder.errors.invalidRatingScale')} (${i + 1})`);
      }
    }

    return errors.length ? errors.join('. ') : null;
  };

  const validationError = useMemo(() => validate(), [title, questions, t]);
  const canSave = useMemo(() => !validationError, [validationError]);

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    
    try {
      type CreateFormPayload = {
        title: string;
        description?: string;
        deadline?: string | null;
        questions: Array<{
          type: FormQuestionType;
          label: string;
          mandatory: boolean;
          options?: { label: string }[];
          meta?: any;
        }>;
        resultsVisibility?: 'PUBLIC' | 'PRIVATE' | 'ADMIN_ONLY';
        participants?: ConnectionIdentifier[];
      };

      const body: CreateFormPayload = {
        title: title.trim(),
        description: description.trim() || undefined,
        deadline: deadline ? deadline.toISOString() : null,
        questions: questions.map((q) => ({
          type: q.type,
          label: q.label.trim(),
          mandatory: q.mandatory,
          options: q.options ? q.options.map((o) => ({ label: o.label.trim() })) : undefined,
          meta: q.type === FormQuestionType.RATING ? { ...(q.meta || {}), scale: q.meta && q.meta.scale !== undefined ? q.meta.scale : 5 } : q.type === FormQuestionType.CHOICE ? { ...(q.meta || {}), maxAnswers: q.meta && q.meta.maxAnswers !== undefined ? Number(q.meta.maxAnswers) : 1 } : q.meta ? q.meta : undefined,
        })),
        resultsVisibility,
        participants: participantMode === 'SELECTED' ? participants : [],
      };

      if (isEdit) {
        // Only warn when questions changed and there are existing responses
        const normalize = (qs: any[]) => qs.map((q: any) => ({
          type: q.type,
          label: q.label ? String(q.label).trim() : '',
          mandatory: !!q.mandatory,
          meta: q.meta ?? null,
          options: q.options ? q.options.map((o: any) => String(o.label).trim()).filter((l: string) => l !== '') : [],
        }));

        const currentNorm = normalize(questions);
        const originalNorm = originalQuestionsNormalized || [];
        const questionsChanged = JSON.stringify(currentNorm) !== JSON.stringify(originalNorm);

        if (responsesCount > 0 && questionsChanged) {
          const confirmed = window.confirm(t('component.formBuilder.resetResponsesConfirm', { count: responsesCount }));
          if (!confirmed) return; // abort save if admin cancels
        }

        await formsService.update(formId!, body);
        toast.success(t('toast.success.communitySaved'));
        if (questionsChanged) {
          setResponsesCount(0);
          setOriginalQuestionsNormalized(currentNorm);
        }
        navigate(`/forms/${formId}`);
      } else {
        const created = await formsService.create(body);
        toast.success(t('toast.success.communitySaved'));
        navigate(`/forms/${created.id}`);
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.error || e?.message || 'Failed to create form');
    }
  };

  const deleteForm = async () => {
    if (!isEdit || !formId) return;
    try {
      await formsService.remove(formId!);
      toast.success(t('warning.success.deleted'));
      setDeleteDialogOpen(false);
      navigate('/forms');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.error || e?.message || 'Failed to delete form');
    }
  }; 

  return (
    <div className="flex flex-col gap-6 items-center">
      <PageHeader
        title={isEdit ? t('page.forms.edit') : t('page.forms.create')}
      />

      <div className="flex flex-col gap-6 w-full max-w-7xl px-6">
        <div className="flex flex-col gap-4 rounded-[12px] bg-secondary-200/70 p-6 w-full max-w-5xl mx-auto">
          <Labeled label={t('component.formBuilder.titleLabel')}>
            <Input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Labeled>

          <Labeled label={t('component.formBuilder.descriptionLabel')}>
            <Input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Labeled>

          <Labeled label={t('component.formResponse.deadlineLabel')}>
            <div className="w-full max-w-md"><DatePicker date={deadline ?? undefined} setDate={(d) => setDeadline(d ?? null)} includeTime /></div>
          </Labeled>

          <Labeled label={t('component.formBuilder.resultsVisibility')}>
            <div className="w-full max-w-sm">
              <DropdownSelect<'PUBLIC'|'PRIVATE'|'ADMIN_ONLY'>
                options={[
                  { label: t('component.formBuilder.resultsVisibilityOptions.public'), value: 'PUBLIC' },
                  { label: t('component.formBuilder.resultsVisibilityOptions.private'), value: 'PRIVATE' },
                  { label: t('component.formBuilder.resultsVisibilityOptions.adminOnly'), value: 'ADMIN_ONLY' },
                ]}
                value={resultsVisibility}
                setValue={(v) => setResultsVisibility((v ?? 'PUBLIC') as 'PUBLIC'|'PRIVATE'|'ADMIN_ONLY')}
              />
            </div>
          </Labeled>

          <Labeled label={t('component.formBuilder.participantMode')}>
            <div className="w-full max-w-sm">
              <DropdownSelect<'EVERYONE'|'SELECTED'>
                options={[
                  { label: t('component.formBuilder.participantModeEveryone'), value: 'EVERYONE' },
                  { label: t('component.formBuilder.participantModeSelected'), value: 'SELECTED' },
                ]}
                value={participantMode}
                setValue={(v) => {
                  setParticipantMode((v ?? 'EVERYONE') as 'EVERYONE'|'SELECTED');
                  if (v === 'EVERYONE') setParticipants([]);
                }}
              />
            </div>
          </Labeled>

          {participantMode === 'SELECTED' && (
            <Labeled label={t('component.formBuilder.participants')}>
              <div className="text-sm text-primary-700 mb-2">{t('component.formBuilder.participantsDescription')}</div>
              <ParticipantsPicker selected={participants} setSelected={(s) => setParticipants(s)} />
            </Labeled>
          )}
        </div>

        {questions.length !== 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={questions.map((q) => q.localId)} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-3 w-full max-w-5xl mx-auto">
                {questions.map((q, idx) => (
                  <SortableQuestion key={q.localId} q={q} idx={idx} total={questions.length} updateQuestion={updateQuestion} removeQuestion={removeQuestion} t={t} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
        <div className="w-full max-w-5xl mx-auto rounded-[12px] border-2 border-dashed border-secondary-200 p-6 text-center">
          <div className="mb-3 font-medium text-primary-900">{t('component.formBuilder.addQuestions')}</div>
          <div className="flex justify-center gap-2">
            <Button variant="primary-outline" className="shadow-sm flex items-center gap-2" onClick={() => addQuestion(FormQuestionType.CHOICE)}><FaPlus className="h-4 w-4" />{t('component.eventPollForm.types.CHOICE')}</Button>
            <Button variant="primary-outline" className="shadow-sm flex items-center gap-2" onClick={() => addQuestion(FormQuestionType.TEXT)}><FaPlus className="h-4 w-4" />{t('component.eventPollForm.types.TEXT')}</Button>
            <Button variant="primary-outline" className="shadow-sm flex items-center gap-2" onClick={() => addQuestion(FormQuestionType.NUMBER)}><FaPlus className="h-4 w-4" />{t('component.eventPollForm.types.NUMBER')}</Button>
            <Button variant="primary-outline" className="shadow-sm flex items-center gap-2" onClick={() => addQuestion(FormQuestionType.RATING)}><FaPlus className="h-4 w-4" />{t('component.eventPollForm.types.RATING')}</Button>
            <Button variant="primary-outline" className="shadow-sm flex items-center gap-2" onClick={() => addQuestion(FormQuestionType.DATE)}><FaPlus className="h-4 w-4" />{t('component.eventPollForm.types.DATE')}</Button>
          </div>
        </div>

        <div className="flex flex-col justify-end gap-2 w-full max-w-5xl mx-auto">
          {validationError && (
            <div className="mb-2 text-left">
              <div className="inline-block w-full rounded bg-secondary-200 p-3 text-sm text-red-700" role="alert" aria-live="assertive">{t('component.formBuilder.errors.fixErrors')}: {validationError}</div>
            </div>
          )}

          <div className="ml-auto flex gap-2">
            <Button onClick={() => navigate('/forms')}>{t('general.cancel')}</Button>
            {isEdit && (
              <Button variant="tertiary" className="p-2 text-red-600 flex items-center gap-2" onClick={() => setDeleteDialogOpen(true)} title={t('general.delete')} aria-label={t('general.delete')}>
                <FaTrash className="h-4 w-4" />{t('general.delete')}
              </Button>
            )} 
            <PrimaryButton text={t('general.save')} onClick={submit} disabled={!canSave} />
          </div>
        </div>

      <ModalLayout open={deleteDialogOpen} setOpen={setDeleteDialogOpen} className="w-fit min-w-[400px] max-w-[600px] bg-surface p-6 rounded-[12px]">
        <div>
          <h2 className="mb-2 text-3xl font-semibold">{t('component.formBuilder.deleteTitle')}</h2>
          <p className="mb-4">{t('component.formBuilder.deleteConfirm', { count: responsesCount })}</p>
        </div>

        <div className="mt-4 flex w-full justify-end">
          <CancelSaveButton
            onCancel={() => setDeleteDialogOpen(false)}
            onSave={deleteForm}
            buttonText={t('general.delete')}
            saveClassName="bg-red-600 text-white"
          />
        </div>
      </ModalLayout>

      </div>
    </div>
  );
}
